import { Contract, providers, utils } from 'ethers'
import { erc20Abi, formatUnits } from 'viem'
import type { PublicClient } from 'viem'
import type { TokenBalance } from './types'

const ERC20_BALANCE_ABI = ['function balanceOf(address) view returns (uint256)']

export function formatTokenAmount(raw: bigint, decimals: number): number {
	return Number.parseFloat(formatUnits(raw, decimals))
}

export function toTokenBalance({
	symbol,
	raw,
	decimals,
	tokenAddress,
}: {
	symbol: string
	raw: bigint
	decimals: number
	tokenAddress?: string
}): TokenBalance {
	const amount = formatTokenAmount(raw, decimals)
	return {
		symbol,
		raw,
		decimals,
		amount,
		formatted: amount.toFixed(2),
		tokenAddress,
	}
}

export async function readViemErc20Balance({
	client,
	tokenAddress,
	account,
	decimals,
	symbol,
}: {
	client: PublicClient
	tokenAddress: `0x${string}`
	account: `0x${string}`
	decimals: number
	symbol: string
}): Promise<TokenBalance> {
	const raw = await client.readContract({
		address: tokenAddress,
		abi: erc20Abi,
		functionName: 'balanceOf',
		args: [account],
	})

	return toTokenBalance({ symbol, raw, decimals, tokenAddress })
}

export async function readEthersErc20Balance({
	rpcUrl,
	tokenAddress,
	account,
	decimals,
	symbol,
}: {
	rpcUrl: string
	tokenAddress: string
	account: string
	decimals: number
	symbol: string
}): Promise<TokenBalance> {
	const provider = new providers.JsonRpcProvider(rpcUrl)
	const token = new Contract(tokenAddress, ERC20_BALANCE_ABI, provider)
	const value = await token.balanceOf(account)
	const raw = BigInt(value.toString())
	const amount = Number(utils.formatUnits(value, decimals))

	return {
		symbol,
		raw,
		decimals,
		amount,
		formatted: amount.toFixed(2),
		tokenAddress,
	}
}
